import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Mail, Phone, Linkedin, MapPin, Send, CheckCircle, Clock, Calendar, MessageSquare } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Load Calendly script and CSS
const useCalendly = () => {
  useEffect(() => {
    // Add Calendly CSS
    const link = document.createElement('link');
    link.href = 'https://assets.calendly.com/assets/external/widget.css';
    link.rel = 'stylesheet';
    document.head.appendChild(link);
    
    // Add Calendly JS
    const script = document.createElement('script');
    script.src = 'https://assets.calendly.com/assets/external/widget.js';
    script.async = true;
    document.body.appendChild(script);
    
    return () => {
      document.head.removeChild(link);
      document.body.removeChild(script);
    };
  }, []);
};

// Calendly popup handler
const openCalendlyPopup = () => {
  if (window.Calendly) {
    window.Calendly.initPopupWidget({
      url: 'https://calendly.com/expertize-edge/30min?hide_event_type_details=1&hide_gdpr_banner=1&primary_color=5c2d91'
    });
  }
};

const ContactPage = () => {
  useCalendly();
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    service_interest: '',
    message: '',
  });
  const [status, setStatus] = useState({ type: '', message: '' });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus({ type: '', message: '' });

    try {
      await axios.post(`${API_URL}/api/contact`, formData);
      setStatus({
        type: 'success',
        message: 'Thank you for your message. We\'ll be in touch within 24 hours!',
      });
      setFormData({
        name: '',
        email: '',
        company: '',
        phone: '',
        service_interest: '',
        message: '',
      });
    } catch (error) {
      setStatus({
        type: 'error',
        message: error.response?.data?.detail || 'Something went wrong. Please try again.',
      });
    } finally {
      setLoading(false);
    }
  };

  const services = [
    'Luminarc™',
    'DPDP Compliance',
    'Consulting & Strategy',
    'Corporate Training',
    'Coaching & Mentorship',
    'Other',
  ];

  const contactMethods = [
    {
      icon: Calendar,
      title: 'Book a Call',
      description: '30-minute discovery session',
      action: 'Schedule Now',
      onClick: openCalendlyPopup,
    },
    {
      icon: Mail,
      title: 'Email Us',
      description: 'contact@expertizeedge.com',
      action: 'Send Email',
      href: 'mailto:contact@expertizeedge.com',
    },
    {
      icon: Phone,
      title: 'Call Us',
      description: '+91 95757 37655',
      action: 'Call Now',
      href: 'tel:+919575737655',
    },
  ];

  return (
    <main className="pt-20">
      {/* Hero */}
      <section className="relative py-32 bg-brand-charcoal overflow-hidden" data-testid="contact-hero">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_50%,rgba(92,45,145,0.3),transparent_50%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_50%,rgba(196,158,77,0.2),transparent_50%)]" />
        </div>
        
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
          <div className="max-w-3xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <span className="inline-flex items-center gap-2 bg-brand-gold/20 border border-brand-gold/30 rounded-full px-4 py-2 mb-6">
                <MessageSquare className="w-4 h-4 text-brand-gold" />
                <span className="text-brand-gold text-sm font-medium">Let's Connect</span>
              </span>
              
              <h1 className="font-heading text-5xl sm:text-6xl font-bold text-white leading-tight mb-6">
                Start Your{' '}
                <span className="text-brand-gold">Transformation</span>{' '}
                Journey
              </h1>
              
              <p className="text-xl text-gray-300 leading-relaxed mb-12">
                Whether you have a specific challenge or want to explore possibilities, 
                we're here to help. Reach out and let's discuss how we can partner together.
              </p>
            </motion.div>
            
            {/* Quick Contact Methods */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="grid grid-cols-1 md:grid-cols-3 gap-4"
            >
              {contactMethods.map((method, index) => (
                <motion.div
                  key={method.title}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 + index * 0.1 }}
                >
                  {method.onClick ? (
                    <button
                      onClick={method.onClick}
                      className="w-full bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl p-6 hover:bg-white/15 transition-all duration-300 group text-left"
                      data-testid="calendly-button"
                    >
                      <method.icon className="w-8 h-8 text-brand-gold mb-3" />
                      <h3 className="text-white font-semibold mb-1">{method.title}</h3>
                      <p className="text-gray-400 text-sm mb-3">{method.description}</p>
                      <span className="text-brand-gold text-sm font-medium group-hover:underline">
                        {method.action} →
                      </span>
                    </button>
                  ) : (
                    <a
                      href={method.href}
                      className="block bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl p-6 hover:bg-white/15 transition-all duration-300 group"
                    >
                      <method.icon className="w-8 h-8 text-brand-gold mb-3" />
                      <h3 className="text-white font-semibold mb-1">{method.title}</h3>
                      <p className="text-gray-400 text-sm mb-3">{method.description}</p>
                      <span className="text-brand-gold text-sm font-medium group-hover:underline">
                        {method.action} →
                      </span>
                    </a>
                  )}
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-24 bg-brand-bg-light" data-testid="contact-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="inline-block bg-brand-purple/10 text-brand-purple text-sm font-semibold px-4 py-2 rounded-full mb-4">
                Send a Message
              </span>
              <h2 className="font-heading text-3xl font-bold text-brand-charcoal mb-4">
                Tell Us About Your <span className="text-brand-purple">Challenge</span>
              </h2>
              <p className="text-brand-muted mb-8">
                Share some details and we'll get back to you within 24 hours.
              </p>

              {status.type === 'success' ? (
                <motion.div 
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="bg-green-50 border border-green-200 rounded-2xl p-8 text-center"
                >
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-8 h-8 text-green-500" />
                  </div>
                  <h3 className="text-xl font-semibold text-green-800 mb-2">Message Sent!</h3>
                  <p className="text-green-700">{status.message}</p>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-brand-body mb-2">
                        Name *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-brand-lavender rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-purple focus:border-transparent bg-white transition-all"
                        placeholder="Your name"
                        data-testid="contact-name"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-brand-body mb-2">
                        Email *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-brand-lavender rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-purple focus:border-transparent bg-white transition-all"
                        placeholder="your@email.com"
                        data-testid="contact-email"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="company" className="block text-sm font-medium text-brand-body mb-2">
                        Company
                      </label>
                      <input
                        type="text"
                        id="company"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-brand-lavender rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-purple focus:border-transparent bg-white transition-all"
                        placeholder="Your company"
                        data-testid="contact-company"
                      />
                    </div>
                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-brand-body mb-2">
                        Phone
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-brand-lavender rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-purple focus:border-transparent bg-white transition-all"
                        placeholder="+91 XXXXX XXXXX"
                        data-testid="contact-phone"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="service_interest" className="block text-sm font-medium text-brand-body mb-2">
                      Service Interest
                    </label>
                    <select
                      id="service_interest"
                      name="service_interest"
                      value={formData.service_interest}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-brand-lavender rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-purple focus:border-transparent bg-white transition-all"
                      data-testid="contact-service"
                    >
                      <option value="">Select a service...</option>
                      {services.map((service) => (
                        <option key={service} value={service}>
                          {service}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-brand-body mb-2">
                      Message *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={5}
                      className="w-full px-4 py-3 border border-brand-lavender rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-purple focus:border-transparent bg-white transition-all resize-none"
                      placeholder="Tell us about your challenge or goals..."
                      data-testid="contact-message"
                    ></textarea>
                  </div>

                  {status.type === 'error' && (
                    <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700">
                      {status.message}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-brand-gold text-brand-charcoal font-semibold py-4 rounded-xl hover:bg-brand-gold-lite hover:scale-[1.02] transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-50 disabled:hover:scale-100"
                    data-testid="contact-submit"
                  >
                    {loading ? (
                      <span>Sending...</span>
                    ) : (
                      <>
                        Send Message
                        <Send className="w-5 h-5" />
                      </>
                    )}
                  </button>
                </form>
              )}
            </motion.div>

            {/* Contact Info & Map */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-8"
            >
              {/* Office Info Card */}
              <div className="bg-white rounded-2xl p-8 shadow-sm border border-brand-lavender" data-testid="contact-details">
                <h3 className="font-heading text-xl font-bold text-brand-charcoal mb-6">
                  Get in Touch
                </h3>
                <div className="space-y-6">
                  <a
                    href="mailto:contact@expertizeedge.com"
                    className="flex items-center gap-4 text-brand-body hover:text-brand-purple transition-colors group"
                  >
                    <div className="w-12 h-12 bg-brand-lavender rounded-xl flex items-center justify-center group-hover:bg-brand-purple/10 transition-colors">
                      <Mail className="w-6 h-6 text-brand-purple" />
                    </div>
                    <div>
                      <p className="font-medium">Email</p>
                      <p className="text-brand-muted">contact@expertizeedge.com</p>
                    </div>
                  </a>
                  <a
                    href="tel:+919575737655"
                    className="flex items-center gap-4 text-brand-body hover:text-brand-purple transition-colors group"
                  >
                    <div className="w-12 h-12 bg-brand-lavender rounded-xl flex items-center justify-center group-hover:bg-brand-purple/10 transition-colors">
                      <Phone className="w-6 h-6 text-brand-purple" />
                    </div>
                    <div>
                      <p className="font-medium">Phone</p>
                      <p className="text-brand-muted">+91 95757 37655</p>
                    </div>
                  </a>
                  <a
                    href="https://linkedin.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-4 text-brand-body hover:text-brand-purple transition-colors group"
                  >
                    <div className="w-12 h-12 bg-brand-lavender rounded-xl flex items-center justify-center group-hover:bg-brand-purple/10 transition-colors">
                      <Linkedin className="w-6 h-6 text-brand-purple" />
                    </div>
                    <div>
                      <p className="font-medium">LinkedIn</p>
                      <p className="text-brand-muted">Follow us for insights</p>
                    </div>
                  </a>
                  <div className="flex items-center gap-4 text-brand-body">
                    <div className="w-12 h-12 bg-brand-lavender rounded-xl flex items-center justify-center">
                      <MapPin className="w-6 h-6 text-brand-purple" />
                    </div>
                    <div>
                      <p className="font-medium">Location</p>
                      <p className="text-brand-muted">Gandhinagar, Gujarat, India</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Response Time */}
              <div className="bg-brand-charcoal rounded-2xl p-8">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 bg-brand-gold/20 rounded-xl flex items-center justify-center">
                    <Clock className="w-6 h-6 text-brand-gold" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold">Quick Response</h3>
                    <p className="text-gray-400 text-sm">We typically respond within 24 hours</p>
                  </div>
                </div>
                <p className="text-gray-400 text-sm leading-relaxed">
                  Our team is dedicated to providing prompt responses. For urgent matters, 
                  please call us directly or book a discovery call.
                </p>
              </div>

              {/* Book Call CTA */}
              <div className="bg-gradient-to-br from-brand-purple to-brand-purple-mid rounded-2xl p-8 text-white" id="booking">
                <h3 className="font-heading text-xl font-bold mb-2">
                  Prefer to Talk?
                </h3>
                <p className="text-white/80 mb-6">
                  Book a 30-minute complimentary discovery call to discuss your needs.
                </p>
                <button
                  onClick={openCalendlyPopup}
                  className="w-full bg-brand-gold text-brand-charcoal font-semibold py-4 rounded-xl hover:bg-brand-gold-lite transition-all duration-300 flex items-center justify-center gap-2"
                >
                  <Calendar className="w-5 h-5" />
                  Let's Connect!
                </button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default ContactPage;
